import { isPlatformBrowser, Location } from '@angular/common';
import { AfterViewInit, Directive, ElementRef, OnDestroy, PLATFORM_ID, effect, inject } from '@angular/core';
import { Router } from '@angular/router';

import { HEADER_DEBUG_HOST } from './header.debug.host';
import { HeaderDebugProbe, HeaderDebugState } from './header.debug.probe';

@Directive({
  selector: 'ush-header',
  standalone: true
})
export class HeaderDebugDirective implements AfterViewInit, OnDestroy {
  private readonly host = inject(HEADER_DEBUG_HOST);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly debugProbe = inject(HeaderDebugProbe);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private hostObserver: MutationObserver | null = null;
  private wrapperObserver: MutationObserver | null = null;

  private readonly onTransitionHandler = (event: Event): void => {
    if (!this.isWrapperEvent(event)) return;

    this.logSnapshot(event.type, {
      propertyName: event instanceof TransitionEvent ? event.propertyName || null : null,
      animationName: event instanceof AnimationEvent ? event.animationName || null : null
    });
  };

  constructor() {
    effect(() => {
      this.host.hasScrolled();
      this.host.isFixed();
      this.host.scrolledStatus();
      this.host.scrollDirection();
      this.host.overContentLg();
      this.host.overContentSm();
      this.host.isOverContentAtTopLg();
      this.host.isOverContentAtTopSm();
      this.host.overContentScrolledLg();
      this.host.overContentScrolledSm();
      this.host.logoFilter();
      this.host.hasDifferentScrolledClass();
      this.host.headerCustomizationClass();
      this.logSnapshot('header_signal_state_change');
    });
  }

  ngAfterViewInit(): void {
    this.logSnapshot('after_view_init');
    this.debugProbe.logSnapshotOnFrames(this.isBrowser, phase => this.logSnapshot(phase), 'after_view_init');
    this.attachEventListeners();
    this.startObservers();
  }

  ngOnDestroy(): void {
    this.hostObserver?.disconnect();
    this.wrapperObserver?.disconnect();
    this.hostObserver = null;
    this.wrapperObserver = null;
    this.detachEventListeners();
  }

  private startObservers(): void {
    const host = this.hostElement.nativeElement;
    const wrapper = host.querySelector('.header-wrapper') as HTMLElement | null;

    this.hostObserver = this.debugProbe.createMutationObserver(this.isBrowser, host, records => {
      this.logSnapshot('host_mutation', {
        mutations: this.summarizeMutations(records)
      });
    });

    this.wrapperObserver = this.debugProbe.createMutationObserver(this.isBrowser, wrapper, records => {
      this.logSnapshot('wrapper_mutation', {
        mutations: this.summarizeMutations(records)
      });
    });
  }

  private attachEventListeners(): void {
    const host = this.hostElement.nativeElement;
    host.addEventListener('transitionstart', this.onTransitionHandler, true);
    host.addEventListener('transitionend', this.onTransitionHandler, true);
    host.addEventListener('animationstart', this.onTransitionHandler, true);
    host.addEventListener('animationend', this.onTransitionHandler, true);
  }

  private detachEventListeners(): void {
    const host = this.hostElement.nativeElement;
    host.removeEventListener('transitionstart', this.onTransitionHandler, true);
    host.removeEventListener('transitionend', this.onTransitionHandler, true);
    host.removeEventListener('animationstart', this.onTransitionHandler, true);
    host.removeEventListener('animationend', this.onTransitionHandler, true);
  }

  private logSnapshot(phase: string, extra: Record<string, unknown> = {}): void {
    this.debugProbe.logSnapshot({
      phase,
      isBrowser: this.isBrowser,
      host: this.hostElement.nativeElement,
      state: this.buildState(),
      extra
    });
  }

  private buildState(): HeaderDebugState {
    return {
      routerUrl: this.router.url,
      locationPath: this.location.path().split('?')[0] || '/',
      hostBindingEnabled: this.host.hostBindingEnabled,
      trackScrollEnabled: this.host.trackScrollEnabled,
      hasScrolled: this.host.hasScrolled(),
      isFixed: this.host.isFixed(),
      scrolledStatus: this.host.scrolledStatus(),
      scrollDirection: this.host.scrollDirection(),
      overContentLg: this.host.overContentLg(),
      overContentSm: this.host.overContentSm(),
      isOverContentAtTopLg: this.host.isOverContentAtTopLg(),
      isOverContentAtTopSm: this.host.isOverContentAtTopSm(),
      overContentScrolledLg: this.host.overContentScrolledLg(),
      overContentScrolledSm: this.host.overContentScrolledSm(),
      logoFilter: this.host.logoFilter(),
      hasDifferentScrolledClass: this.host.hasDifferentScrolledClass(),
      headerCustomizationClass: this.host.headerCustomizationClass()
    };
  }

  private summarizeMutations(records: MutationRecord[]): Array<Record<string, unknown>> {
    return records.map(record => ({
      attributeName: record.attributeName,
      targetClassName: record.target instanceof HTMLElement ? record.target.className || null : null
    }));
  }

  private isWrapperEvent(event: Event): boolean {
    return event.target instanceof HTMLElement && event.target.classList.contains('header-wrapper');
  }
}
